import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useMemo, useRef, useState } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { useSpeakerHypothesisActions } from "../../hooks/useSpeakerHypothesisActions"
import { useSpeakerRosterSearch } from "../../hooks/useSpeakerRosterSearch"
import {
  discardSpeaker,
  discardSpeakerEntity,
  fetchSpeakerLines,
  type ISpeakerMergeCandidate,
  linkSpeakerEntity,
  mergeSpeakerEntities,
  patchSpeakerProfile,
  renameSpeaker,
  renameSpeakerEntity,
} from "../../lib/api"
import { extractErrorMessage } from "../../lib/api/errors"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import { applySpeakerOptimistic } from "../../lib/speaker-optimistic"
import { buildMergeOptions, speakerDisplayLabel, speakerEntityId, speakerRosterKey } from "../../lib/speaker-vm"
import type { IMeetingDetail } from "../../lib/types/meeting"
import type { IProps } from "./interfaces"
import { SpeakerCardView } from "./SpeakerCardView"
import type { TSpeakerEditIntent } from "./SpeakerCardView/interfaces"

/**
 * Smart wrapper for SpeakerCard. Owns the company API client and the speaker
 * mutations, choosing the entity-id routes when the VM carries an `id` and
 * falling back to the legacy name-keyed shims otherwise. It also owns the
 * rename → merge flow: an entity rename onto an existing name comes back as a
 * merge candidate, which we confirm and turn into a merge. SpeakerCardView is
 * the pure render surface.
 */
export function SpeakerCard({
  meetingId,
  speaker,
  allSpeakers,
  quotes,
  onRenamed,
  profile,
  autoColor,
  autoTitle,
  defaultOpen = false,
  speakerSegments,
  audioUrl,
}: IProps): React.JSX.Element {
  const company = useCompanyApi()
  const { i18n } = useLingui()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const hypothesis = useSpeakerHypothesisActions(meetingId)
  const [fallbackLines, setFallbackLines] = useState<string[]>([])
  const [loadingLines, setLoadingLines] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingProfile, setSavingProfile] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const [error, setError] = useState("")
  const [rosterQuery, setRosterQuery] = useState("")
  const rosterSearch = useSpeakerRosterSearch(rosterQuery, true)

  // Identity plumbing derived once from the VM:
  //  - rosterKey  → the name/label the legacy shims + profile route + the
  //    hypothesis label key on (raw label while unresolved, bare name once named).
  //  - entityId   → the entity-route key; undefined for a legacy meeting or an
  //    entity projection slot with no matching entity → falls back to the shims.
  const rosterKey = speakerRosterKey(speaker)
  const entityId = speakerEntityId(speaker)
  const isUnknown = speaker.status === "unidentified"
  const suggestion = speaker.suggestion
  const mergeOptions = useMemo(
    () => buildMergeOptions(allSpeakers, speaker.displayName),
    [allSpeakers, speaker.displayName],
  )

  // The user-facing label (unified "Speaker N" for an unidentified slot) — the raw
  // display name stays the identity the mutations key on. Presence only surfaces on
  // a hybrid meeting (some speaker joined remotely / rode the far-end channel).
  const displayLabel = speakerDisplayLabel(speaker, i18n)
  const showPresence = useMemo(() => allSpeakers.some((vm) => vm.presence === "remote" || vm.farEnd), [allSpeakers])

  // A rename/discard/merge/profile edit here changes a speaker name; every surface
  // that paints it (detail page, transcript, list card) must refresh. The
  // existing `onRenamed` callback stays — hosts use it to close the modal — but
  // it doesn't own the cross-query invalidation, so do it here at the source.
  const invalidateSpeakerCaches = useCallback((): void => {
    queryClient.invalidateQueries({ queryKey: queryKeys.meeting(orgId, meetingId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.transcript(orgId, meetingId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.meetings(orgId) })
  }, [queryClient, orgId, meetingId])

  // Load-once latch, keyed by speaker: the old guard keyed on
  // `fallbackLines.length > 0`, so a speaker whose lines come back EMPTY (e.g. a
  // stale entity with no segments) never satisfied it and any re-triggering
  // caller refired the fetch forever — observed 2026-07-22 as a request loop on
  // a label-less zombie speaker. Latch on "a fetch completed for THIS speaker",
  // not "we got content"; a speaker change naturally re-arms the fetch.
  const linesLoadedForRef = useRef<string | null>(null)
  const handleLoadLines = useCallback(async (): Promise<void> => {
    if (linesLoadedForRef.current === rosterKey || loadingLines || !company) return
    setLoadingLines(true)
    try {
      const data = await fetchSpeakerLines(company, meetingId, rosterKey, 5)
      setFallbackLines(data.lines)
      linesLoadedForRef.current = rosterKey
    } catch {
      setFallbackLines([i18n._(msg`(Could not load lines)`)])
      linesLoadedForRef.current = rosterKey
    } finally {
      setLoadingLines(false)
    }
  }, [company, meetingId, rosterKey, loadingLines, i18n])

  const finishEdit = useCallback(
    (message: string): void => {
      notifications.show({ message, color: "success" })
      invalidateSpeakerCaches()
      onRenamed?.()
    },
    [invalidateSpeakerCaches, onRenamed],
  )

  const patchSpeakers = useCallback(
    (patch: Parameters<typeof applySpeakerOptimistic>[1]): IMeetingDetail | undefined => {
      const meetingKey = queryKeys.meeting(orgId, meetingId)
      const previous = queryClient.getQueryData<IMeetingDetail>(meetingKey)
      if (previous?.speakers) {
        queryClient.setQueryData<IMeetingDetail>(meetingKey, {
          ...previous,
          speakers: applySpeakerOptimistic(previous.speakers, patch),
        })
      }
      return previous
    },
    [meetingId, orgId, queryClient],
  )

  // Renaming onto an existing name: confirm, then fold this speaker's entity into
  // the peer. `openConfirmModal` is callback-based; wrap it in a Promise so the
  // rename flow can await the decision inline. Resolves false on cancel/close.
  const confirmMerge = useCallback(
    (candidate: ISpeakerMergeCandidate): Promise<boolean> =>
      new Promise((resolve) => {
        modals.openConfirmModal({
          title: i18n._(msg`Merge speakers`),
          children: (
            <>
              {i18n._(
                msg`"${candidate.displayName}" already exists — merge this speaker into ${candidate.displayName}? Their transcript lines and voice samples combine, and it can't be undone.`,
              )}
            </>
          ),
          labels: { confirm: i18n._(msg`Merge`), cancel: i18n._(msg`Cancel`) },
          confirmProps: { color: "danger" },
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
          onClose: () => resolve(false),
        })
      }),
    [i18n],
  )

  const handleSubmit = useCallback(
    async (intent: TSpeakerEditIntent): Promise<void> => {
      if (!company) return
      setSaving(true)
      setError("")
      try {
        const fromLabel = speakerDisplayLabel(speaker, i18n)
        if (intent.kind === "merge") {
          // Entity merge when both ends carry an id; else the legacy shim, which
          // merges by renaming this speaker onto the (existing) target name.
          if (entityId !== undefined && intent.targetEntityId !== undefined) {
            await mergeSpeakerEntities(company, meetingId, intent.targetEntityId, [entityId])
          } else {
            await renameSpeaker(company, meetingId, rosterKey, intent.targetName)
          }
          finishEdit(i18n._(msg`Merged ${fromLabel} into ${intent.targetName}.`))
          return
        }

        if (intent.kind === "link") {
          if (entityId === undefined) {
            await renameSpeaker(company, meetingId, rosterKey, intent.name)
            finishEdit(i18n._(msg`Renamed ${fromLabel} to ${intent.name}.`))
            return
          }
          const previous = patchSpeakers({
            kind: "link",
            entityId,
            label: speaker.label,
            name: intent.name,
            speakerId: intent.speakerId,
          })
          try {
            await linkSpeakerEntity(company, meetingId, entityId, intent.speakerId)
            finishEdit(i18n._(msg`Named ${fromLabel} as ${intent.name}.`))
          } catch (err) {
            if (previous) queryClient.setQueryData(queryKeys.meeting(orgId, meetingId), previous)
            throw err
          }
          return
        }

        // Rename. The entity route reports a name collision as a merge candidate
        // rather than a hard error, so we offer to merge into the existing speaker.
        if (entityId !== undefined) {
          const previous = patchSpeakers({ kind: "rename", entityId, label: speaker.label, name: intent.name })
          const result = await renameSpeakerEntity(company, meetingId, entityId, intent.name)
          if (result.ok) {
            finishEdit(i18n._(msg`Renamed ${fromLabel} to ${intent.name}.`))
            return
          }
          if (previous) queryClient.setQueryData(queryKeys.meeting(orgId, meetingId), previous)
          const confirmed = await confirmMerge(result.mergeCandidate)
          if (!confirmed) return
          await mergeSpeakerEntities(company, meetingId, result.mergeCandidate.entityId, [entityId])
          finishEdit(i18n._(msg`Merged ${fromLabel} into ${result.mergeCandidate.displayName}.`))
          return
        }

        // Legacy meeting (no entity id) — the name-keyed shim resolves + renames.
        await renameSpeaker(company, meetingId, rosterKey, intent.name)
        finishEdit(i18n._(msg`Renamed ${fromLabel} to ${intent.name}.`))
      } catch (err) {
        const message = extractErrorMessage(err)
        setError(message)
        notifyError(err)
        // Rethrow so the view keeps the typed draft + stays expanded; clearing
        // the form only happens in the view's success branch.
        throw err
      } finally {
        setSaving(false)
      }
    },
    [
      company,
      meetingId,
      entityId,
      rosterKey,
      speaker,
      confirmMerge,
      finishEdit,
      i18n,
      orgId,
      patchSpeakers,
      queryClient,
    ],
  )

  const handleSaveProfile = useCallback(
    async ({ title, color }: { title: string | null; color: string | null }): Promise<void> => {
      if (!company) return
      setSavingProfile(true)
      setError("")
      try {
        // Profile stays name-keyed — the route resolves the display name to its entity.
        await patchSpeakerProfile(company, meetingId, rosterKey, { color, title })
        invalidateSpeakerCaches()
        onRenamed?.()
      } catch (err) {
        setError(extractErrorMessage(err))
        notifyError(err)
      } finally {
        setSavingProfile(false)
      }
    },
    [company, meetingId, rosterKey, onRenamed, invalidateSpeakerCaches],
  )

  const handleDiscard = useCallback(async (): Promise<void> => {
    if (!company) return
    setDiscarding(true)
    setError("")
    try {
      if (entityId !== undefined) {
        await discardSpeakerEntity(company, meetingId, entityId)
      } else {
        await discardSpeaker(company, meetingId, rosterKey)
      }
      finishEdit(i18n._(msg`Discarded ${speakerDisplayLabel(speaker, i18n)} — their lines are now marked Unknown.`))
    } catch (err) {
      const message = extractErrorMessage(err)
      setError(message)
      notifyError(err)
    } finally {
      setDiscarding(false)
    }
  }, [company, meetingId, entityId, rosterKey, speaker, finishEdit, i18n])

  // Suggestion confirm/dismiss goes through the shared owner. The `rosterKey`
  // doubles as the diarisation label the suggestion is keyed on — a
  // still-unidentified speaker keeps its raw label in the roster, so the two
  // are the same string. The hook invalidates the caches on success, so the
  // banner disappears as the refreshed status stops being "suggested".
  const handleConfirmSuggestion = useCallback((): void => {
    if (!suggestion) return
    void hypothesis.onAction(rosterKey, suggestion.name, "confirm")
  }, [hypothesis, rosterKey, suggestion])

  const handleRejectSuggestion = useCallback((): void => {
    if (!suggestion) return
    void hypothesis.onAction(rosterKey, suggestion.name, "reject")
  }, [hypothesis, rosterKey, suggestion])

  const suggestionBusy = suggestion
    ? hypothesis.pending.has(`${rosterKey}|${suggestion.name}|confirm`) ||
      hypothesis.pending.has(`${rosterKey}|${suggestion.name}|reject`)
    : false

  return (
    <SpeakerCardView
      speakerDisplayName={displayLabel}
      mergeOptions={mergeOptions}
      quotes={quotes}
      fallbackLines={fallbackLines}
      loadingLines={loadingLines}
      isUnknown={isUnknown}
      presence={speaker.presence}
      showPresence={showPresence}
      profile={profile}
      autoColor={autoColor}
      autoTitle={autoTitle}
      defaultOpen={defaultOpen}
      knownVoiceId={speaker.speakerId}
      speakerSegments={speakerSegments}
      audioUrl={audioUrl}
      saving={saving}
      savingProfile={savingProfile}
      discarding={discarding}
      error={error}
      onLoadLines={handleLoadLines}
      onSubmit={handleSubmit}
      onSaveProfile={handleSaveProfile}
      onDiscard={handleDiscard}
      suggestion={suggestion}
      onConfirmSuggestion={handleConfirmSuggestion}
      onRejectSuggestion={handleRejectSuggestion}
      suggestionBusy={suggestionBusy}
      rosterPeople={rosterSearch.people}
      rosterLoading={rosterSearch.isFetching}
      onRosterQueryChange={setRosterQuery}
    />
  )
}
